<template>
	<InputAmount
		:header-title="$t('withdrawal-create.amount-wrapper.title')"
		v-model="amount"
		:currency="currentAmountCurrency"
		@update:focused="handleFocusInputAmount"
		@blur="handleBlurInputAmount"
        :disabled="isSubmitLoading"
	>
		<template v-if="!isMaxLessMinRates" #header-end>
			<InputAmountRange
				:ranges="minMaxRangesAmountByRequisite"
				:currency="currentAmountCurrency"
				:is-loading="activeRequisiteMinMaxLoading"
				:is-error="isAgentsRangeError"
			/>
		</template>
		<template #content>
			<Transition name="fade" mode="out-in">
				<InputAmountButton
					v-if="isShowMinMaxRangeByRequisite && !isMaxLessMinRates"
					@click="handleClickMaxInputAmount"
					text="MAX"
				/>
			</Transition>
		</template>
		<template #hint>
            <p v-if="isBalanceLessMinWithdrawal" class="withdrawal-input-amount-error-hint">
                {{ $t('withdrawal-create.errors.amount.balance', { value: formattedNotEnoughFundsMinAmount }) }}
            </p>
            <p v-else-if="isAmountGreaterThanBalance" class="withdrawal-input-amount-error-hint">
                {{ $t('withdrawal-create.errors.amount.less-balance', { value: formattedBalanceAmount })}}
            </p>
			<p v-else-if="isMaxLessMinRates" class="withdrawal-input-amount-error-hint">
				{{ $t('withdrawal-create.errors.ranges.max-less-min') }}
			</p>
			<p v-else-if="isP2PRequisite || !activeRequisite">≈ <span>{{ formattedConvertedInputAmount }}</span></p>
			<p v-else>{{ $t('withdrawal-create.fee') }}</p>
		</template>
	</InputAmount>
</template>

<script setup lang="ts">
import InputAmount from "@components/ui/input-amount/InputAmount.vue";
import InputAmountButton from "@components/ui/input-amount/InputAmountButton.vue";
import InputAmountRange from "@components/ui/input-amount/InputAmountRange.vue";
import WithdrawalPopupController from "@/views/withdrawal-popup-group/WithdrawalPopupController";
import {getDevice} from "framework7/shared/get-device";
import {computed} from "vue";
import UtilsService from "@/services/others/UtilsService";

const viewWithdrawalController = WithdrawalPopupController.getInstance();

const {
	amount,
	currentAmountCurrency,
	textMaxMinError,
	activeRequisiteMinMaxLoading,
	inputAmountFocused,
	minMaxRangesAmountByRequisite,
	isP2PRequisite,
	activeRequisite,
	rates,
	account,
    isSubmitLoading,
    isAgentsRangeError,
	isBalanceLessMinWithdrawal,
	isAmountGreaterThanBalance,
	isMaxLessMinRates,
	notEnoughFundsMinAmount,
} = viewWithdrawalController;

const isShowMinMaxRangeByRequisite = computed(() => {
	const { min, max } = minMaxRangesAmountByRequisite.value;
	return !(!min || !max);
});

const currencyFormatter = computed(() => UtilsService.of().currencyFormatter(account.value.currency.name));

const formattedConvertedInputAmount = computed(() => {
	const floorAmount = Math.floor(amount.value * rates.value.sell);

	return currencyFormatter.value.format(floorAmount);
});

const currencyUSDFormatter = computed(() => UtilsService.of().currencyFormatter('USD'));
const formattedBalanceAmount = computed(() => currencyUSDFormatter.value.format(account.value.balance.amount));
const formattedNotEnoughFundsMinAmount = computed(() => currencyUSDFormatter.value.format(notEnoughFundsMinAmount.value));

const handleFocusInputAmount = (value: boolean) => {
	textMaxMinError.value = false;
	inputAmountFocused.value = value;
}

const handleBlurInputAmount = () => {
	const timeout = getDevice().cordova ? 314 : 0;

	setTimeout(() => {
		inputAmountFocused.value = false;
	}, timeout);

	if (amount.value === 0) {
		viewWithdrawalController.checkRequisiteMinMaxAndSetInput();
	}
}

const handleClickMaxInputAmount = () => {
	amount.value = minMaxRangesAmountByRequisite.value.max;
	textMaxMinError.value = false;
}
</script>

<style scoped lang="scss">
.withdrawal-input-amount-error-hint {
	color: var(--red);
}
</style>
